namespace $.$$ {
	export class $shm_hitalama_speech extends $.$shm_hitalama_speech {
		
		enabled_visible() {
			return this.hearing() ? super.enabled_visible() : []
		}

		@ $mol_mem
		hearing( next?: boolean ): boolean {
			if( next ) {
				this.start_recording()
			} else if ( next === false ) {
				this.stop_recording()
			}
			return next ?? false
		}
		
		@ $mol_mem
		audio_blob( next?: Blob ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_mem
		recording_state( next?: 'inactive' | 'recording' | 'paused' ) {
			$mol_wire_solid()
			return next ?? 'inactive'
		}

		@ $mol_mem
		media_recorder( next?: MediaRecorder | null ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_mem
		recording_id( next?: string | null ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_mem
		recording_widget_done( next?: string | null ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_mem
		recording_file_name( next?: string | null ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_mem
		recording_widget( next?: $shm_hitalama_map_widget | null ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_mem
		recording_map_file( next?: $shm_hitalama_file | null ) {
			$mol_wire_solid()
			return next ?? null
		}

		@ $mol_action
		start_recording() {
			if( this.media_recorder() ) return

			$mol_wire_async( this ).start_recording_later().catch( error => {
				this.start_recording_failed( error )
			} )
		}

		start_recording_later() {
			const deviceId = $shm_hitalama_settings.mic()

			return this.user_media( deviceId )
				.catch( error => {
					if( !deviceId ) throw error
					$shm_hitalama_settings.mic( '' )
					return this.user_media( '' )
				} )
				.then( stream => this.recording_start( stream ) )
		}

		user_media( deviceId: string ) {
			const audio = {
				echoCancellation: true,
				noiseSuppression: true,
				...( deviceId ? { deviceId: { ideal: deviceId } } : {} ),
			}

			return navigator.mediaDevices.getUserMedia({ audio })
		}

		mime_type() {
			const supported = [
				'audio/webm;codecs=opus',
				'audio/webm',
				'audio/ogg;codecs=opus',
				'audio/ogg',
			].find( mime => MediaRecorder.isTypeSupported( mime ) )

			return supported ?? ''
		}

		@ $mol_action
		recording_start( stream: MediaStream ) {
			const recording_id = `${ Date.now() }:${ Math.random() }`
			const mime = this.mime_type()
			const options = mime ? { mimeType: mime } : undefined
			const date = new $mol_time_moment()
			const ext = mime.includes( 'webm' ) ? 'webm' : 'ogg'

			let recorder: MediaRecorder
			try {
				recorder = new MediaRecorder( stream, options )
			} catch( error ) {
				stream.getTracks().forEach( track => track.stop() )
				throw error
			}
			this.recording_id( recording_id )
			this.recording_widget_done( null )
			this.recording_file_name( `recording-${ date.toString() }.${ ext }` )
			this.recording_widget( null )
			this.recording_map_file( null )
			this.media_recorder( recorder )
			
			const chunks: BlobPart[] = []
			recorder.ondataavailable = ( e ) => {
				if( e.data.size ) chunks.push( e.data )
			}
			recorder.onstop = () => {
				try {
					this.onstop( recording_id, chunks, recorder.mimeType || mime || 'audio/webm' )
				} catch( error ) {
					this.recording_stopped()
					if( $mol_promise_like( error ) ) return
					$mol_fail_log( error )
				} finally {
					stream.getTracks().forEach( track => track.stop() )
				}
			}

			recorder.start( 1000 )
			this.recording_state( 'recording' )
		}

		@ $mol_action
		start_recording_failed( error: unknown ) {
			this.media_recorder( null )
			this.recording_state( 'inactive' )
			this.hearing( false )
			$mol_fail_log( error )
		}

		@ $mol_action
		onstop( recording_id: string, chunks: BlobPart[], mime = 'audio/ogg; codecs=opus' ) {
			if( this.recording_widget_done() === recording_id ) return

			const blob = new Blob( chunks, { type: mime } )
			this.audio_blob( blob )
			this.recording_stopped()

			const map = $shm_hitalama_app.Root(0).map()
			const pos = $shm_hitalama_app.Root(0).Map().Map_page().get_center_pos()
			const name = this.recording_file_name() ?? `recording-${ new $mol_time_moment().toString() }.webm`

			const file = new File([blob], name, {
				lastModified: Date.now(),
				type: mime,
			})
			this.recording_file_widget( map, [pos[0]-250,pos[1]-75], file, 500, 150 )
			this.recording_widget_done( recording_id )
		}

		recording_file_widget(
			map: $shm_hitalama_map,
			pos: readonly [number, number] | readonly number[],
			file: File,
			width: number,
			height: number,
		) {
			let widget = this.recording_widget()
			if( !widget ) {
				widget = map.widget_add( 'file', pos, width, height, file.name )
				if( !widget ) return null
				this.recording_widget( widget )
			}

			let map_file = this.recording_map_file()
			if( !map_file ) {
				map_file = map.file_make()!
				this.recording_map_file( map_file )
			}

			map_file.write( file )
			widget.File(null)!.remote( map_file )
			widget.title( file.name )
			if( $shm_hitalama_map_widget_entity_app.archive( map_file ) ) widget.app()
			return widget
		}

		@ $mol_action
		recording_stopped() {
			this.media_recorder( null )
			this.recording_state( 'inactive' )
			this.hearing( false )
			this.recording_id( null )
		}

		@ $mol_action
		stop_recording() {
			const recorder = this.media_recorder()
			if( !recorder ) return
			if( recorder.state === 'inactive' ) return

			try {
				if( recorder.state === 'recording' ) recorder.requestData()
			} catch( error ) {
				$mol_fail_log( error )
			}

			recorder.stop()
		}

		@ $mol_action
		pause_recording() {
			const recorder = this.media_recorder()
			if( recorder && this.recording_state() === 'recording' ) {
				recorder.pause()
				this.recording_state( 'paused' )
			}
		}

		@ $mol_action
		resume_recording() {
			const recorder = this.media_recorder()
			if( recorder && this.recording_state() === 'paused' ) {
				recorder.resume()
				this.recording_state( 'recording' )
			}
		}

		@ $mol_action
		download_audio() {
			const blob = this.audio_blob()
			if( !blob ) return

			const url = URL.createObjectURL( blob )
			const a = document.createElement( 'a' )
			a.href = url
			a.download = `recording-${new Date().toISOString()}.ogg`
			a.click()
			URL.revokeObjectURL( url )
		}
		
	}
}
